import { PERMISSIONS } from '@ibe/constants';
import { Button, StatusBadge } from '@ibe/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ImportDialog } from '../../../components/import-dialog/import-dialog.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { PermissionGuard } from '../../../auth/permission-guard.js';
import { stateRowSchema } from '../api/schemas.js';

/**
 * The states screen.
 *
 * Filtered by country: a state code is unique within its country and
 * meaningless without it, so the list is always narrowed to one.
 */
export function StateListPage(): React.JSX.Element {
  const [importing, setImporting] = useState(false);

  return (
    <>
      <ResourceListPage
        title="States"
        description="ISO 3166-2 subdivisions. Filter by country, search by name or code."
        path="/v1/states"
        resource="states"
        schema={stateRowSchema}
        permission={PERMISSIONS.REFERENCE_READ}
        rowKey={(row) => row.id}
        defaultSort={{ columnId: 'name', direction: 'asc' }}
        filters={[
          {
            id: 'countryCode',
            label: 'Country',
            param: 'countryCode',
            kind: 'entity',
            optionsPath: '/v1/countries/options',
          },
        ]}
        actions={
          <PermissionGuard permission={PERMISSIONS.REFERENCE_IMPORT}>
            <Button
              onClick={() => {
                setImporting(true);
              }}
            >
              Import
            </Button>
          </PermissionGuard>
        }
        columns={[
          {
            id: 'code',
            header: 'Code',
            sortable: true,
            fields: ['code'],
            cell: (row) => (
              <Link
                className="font-medium underline-offset-4 hover:underline"
                to={`/states/${row.id}`}
              >
                {row.code}
              </Link>
            ),
          },
          { id: 'name', header: 'Name', sortable: true, fields: ['name'], cell: (row) => row.name },
          {
            id: 'countryName',
            header: 'Country',
            fields: ['countryName'],
            cell: (row) => row.countryName ?? '—',
          },
          {
            id: 'isActive',
            header: 'Status',
            fields: ['isActive'],
            cell: (row) => (
              <StatusBadge tone={row.isActive === true ? 'success' : 'neutral'}>
                {row.isActive === true ? 'Active' : 'Inactive'}
              </StatusBadge>
            ),
          },
        ]}
      />

      <ImportDialog
        resource="states"
        basePath="/v1/states"
        open={importing}
        onClose={() => {
          setImporting(false);
        }}
      />
    </>
  );
}
